import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { setMobileSession, isValidMobileSession } from '../../Utils/mobileAuth';
import { PatientReportIndex } from '../patients/components/patientReport';
import { getReportDataById } from '../../rtk-apis/reports/getReportDataById';
import { changeReportStatus } from '../../rtk-apis/reports/changeReportStatus';
import { clearChangeReportStatus } from '../../store/slices/reports/changeReportStatus';
import {
  canChangeReportStatus,
  canUnfinalizeReport,
} from '../../Utils/reportPermissions';
import { AppDispatch, RootState } from '../../store/store';
import './mobile.scss';

const STATUS_CHIP_COLOR: Record<string, 'default' | 'warning' | 'success'> = {
  Draft: 'default',
  Preliminary: 'warning',
  Finalized: 'success',
};

/** Next logical status for the "promote" action. */
const nextStatus = (current: string): string | null => {
  if (current === 'Draft') return 'Preliminary';
  return null;
};

const AUTH_TIMEOUT_MS = 10_000;

const MobileReportEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const [authReady, setAuthReady] = useState(isValidMobileSession());
  const [authError, setAuthError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<any>({});
  const [updatedTemplateContent, setUpdatedTemplateContent] = useState<any>({});
  const [statusChanging, setStatusChanging] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const { data: fetchedReport, loading: reportLoading } = useSelector(
    (state: RootState) => state.getReportDataByIdResponse
  );

  // ── Auth bridge ──────────────────────────────────────────────────────────────
  // React Native sends { type: 'MOBILE_AUTH', token, hospitalId, userId, role, name }
  // after the WebView fires onLoad. We signal readiness first so RN knows when to send.
  useEffect(() => {
    if (authReady) return;

    const timer = setTimeout(() => {
      setAuthError('Authentication timed out. Close and reopen the report.');
    }, AUTH_TIMEOUT_MS);

    const handler = (event: MessageEvent) => {
      try {
        const msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (!msg || msg.type !== 'MOBILE_AUTH') return;
        const { token, hospitalId, userId, role, name } = msg;
        if (!token) return;
        setMobileSession(token, hospitalId ?? '', userId ?? '', role ?? '', name ?? '');
        clearTimeout(timer);
        setAuthReady(true);
      } catch {
        // ignore non-JSON messages (TipTap internal, etc.)
      }
    };

    window.addEventListener('message', handler);

    // Notify React Native that the JS bridge is ready to receive the auth payload
    try {
      (window as any).ReactNativeWebView?.postMessage(JSON.stringify({ type: 'MOBILE_READY' }));
    } catch {
      // non-RN environments — ignore
    }

    return () => {
      window.removeEventListener('message', handler);
      clearTimeout(timer);
    };
  }, [authReady]);

  // ── Report fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authReady || !id) return;
    const hospitalId = sessionStorage.getItem('hospitalId') ?? '';
    dispatch(getReportDataById({ reportId: id, hospitalId }));
  }, [authReady, id, dispatch]);

  useEffect(() => {
    if (fetchedReport && Object.keys(fetchedReport).length > 0) {
      setReportData(fetchedReport);
    }
  }, [fetchedReport]);

  // ── Status change ─────────────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus: string) => {
    if (!reportData?.data?._id || !id) return;
    setStatusChanging(true);
    setStatusMsg(null);
    try {
      await dispatch(
        changeReportStatus({
          reportId: reportData.data._id,
          reportStatus: newStatus,
          templateHTML: updatedTemplateContent,
        } as any)
      ).unwrap();
      // Re-fetch to get updated status
      const hospitalId = sessionStorage.getItem('hospitalId') ?? '';
      dispatch(getReportDataById({ reportId: id, hospitalId }));
      setStatusMsg({ text: `Marked as ${newStatus}`, ok: true });
    } catch (e: any) {
      setStatusMsg({ text: e?.message ?? 'Status change failed', ok: false });
    } finally {
      setStatusChanging(false);
      dispatch(clearChangeReportStatus());
    }
  };

  // ── Render: error / loading states ───────────────────────────────────────────
  if (authError) {
    return (
      <div className="mobile-report__error">
        <p>{authError}</p>
      </div>
    );
  }

  if (!authReady) {
    return (
      <div className="mobile-report__loading">
        <CircularProgress size={32} />
        <p>Connecting...</p>
      </div>
    );
  }

  if (reportLoading || !reportData?.data?._id) {
    return (
      <div className="mobile-report__loading">
        <CircularProgress size={32} />
        <p>Loading report...</p>
      </div>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────────
  const role = sessionStorage.getItem('role') ?? '';
  const userId = sessionStorage.getItem('userId') ?? '';
  const status: string = reportData?.data?.status ?? 'Draft';
  const finalizedById: string | undefined = reportData?.data?.finalizedById;

  const promote = nextStatus(status);
  const canPromote = canChangeReportStatus(role, status) && !!promote;
  const canUnfinalize = canUnfinalizeReport(role, status, finalizedById, userId);
  const showActions = canPromote || canUnfinalize;

  return (
    <div className="mobile-report">
      <div className="mobile-report__header">
        <div className="mobile-report__header-info">
          <span className="mobile-report__patient-name">
            {reportData?.data?.name ?? 'Report'}
          </span>
          <Chip
            label={status}
            color={STATUS_CHIP_COLOR[status] ?? 'default'}
            size="small"
          />
        </div>
        {statusMsg && (
          <div
            className="mobile-report__status-msg"
            style={{ color: statusMsg.ok ? '#2e7d32' : '#c62828' }}
          >
            {statusMsg.text}
          </div>
        )}
      </div>

      <div className="mobile-report__editor">
        <PatientReportIndex
          reportData={reportData}
          setHitGetReportApi={() => {}}
          setUpdatedTemplateContent={setUpdatedTemplateContent}
        />
      </div>

      {showActions && (
        <div className="mobile-report__actions">
          {canPromote && promote && (
            <Button
              variant="contained"
              fullWidth
              disabled={statusChanging}
              onClick={() => handleStatusChange(promote)}
            >
              {statusChanging ? 'Saving...' : `Mark as ${promote}`}
            </Button>
          )}
          {canUnfinalize && (
            <Button
              variant="outlined"
              fullWidth
              disabled={statusChanging}
              onClick={() => handleStatusChange('Preliminary')}
            >
              {statusChanging ? 'Saving...' : 'Unfinalize'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileReportEditor;
